import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import RichContent from '../components/RichContent';
import RichTextEditor from '../components/RichTextEditor';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import FramedAvatar from '../components/FramedAvatar';
import DateTimePicker from '../components/DateTimePicker';
import {
  ArrowLeft, Calendar as CalendarIcon, Clock, MapPin, Video, Users, 
  ExternalLink, MessageSquare, Send, CornerDownRight, XCircle,
  Play, Edit, Trash2, UserCheck, UserX, ChevronDown
} from 'lucide-react';

const EventDetailPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [calendarLinks, setCalendarLinks] = useState(null);
  const [showCalendarLinks, setShowCalendarLinks] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [replayUrl, setReplayUrl] = useState('');
  const [replayDialogOpen, setReplayDialogOpen] = useState(false);
  const [seriesDialogOpen, setSeriesDialogOpen] = useState(false);
  const [seriesAction, setSeriesAction] = useState(null);

  const now = new Date();

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (event) {
      fetchParticipants();
      fetchComments();
      fetchCalendarLinks();
    }
  }, [event?.id]);

  const fetchEvent = async () => {
    try {
      const res = await api.get('/events');
      const found = res.data.find(e => e.id === eventId);
      if (found) {
        setEvent(found);
      } else {
        toast.error('Evenement non trouve');
        navigate('/calendar');
      }
    } catch (e) {
      toast.error('Erreur');
      navigate('/calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const res = await api.get(`/events/${eventId}/participants`);
      setParticipants(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setParticipants([]); }
  };

  const fetchComments = async () => {
    try {
      const res = await api.get(`/events/${eventId}/comments`);
      setComments(res.data || []);
    } catch (e) { setComments([]); }
  };

  const fetchCalendarLinks = async () => {
    try {
      const res = await api.get(`/events/${eventId}/calendar-links`);
      setCalendarLinks(res.data);
    } catch (e) {}
  };

  const isParticipating = participants.some(p => p.user_id === user.id);
  const startAt = event?.start_time ? parseISO(event.start_time) : null;
  const endAt = event?.end_time ? parseISO(event.end_time) : null;
  const isLive = startAt && endAt && isBefore(startAt, now) && isBefore(now, endAt);
  const isPast = endAt && isBefore(endAt, now);
  const isAdmin = user.role === 'admin' || event?.created_by === user.id;

  const handleParticipate = async () => {
    try {
      if (isParticipating) {
        await api.delete(`/events/${eventId}/attend`);
        toast.success('Participation annulee');
      } else {
        await api.post(`/events/${eventId}/attend`, { event_id: eventId });
        toast.success('Tu participes !');
      }
      fetchParticipants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/events/${eventId}/comments`, { content: newComment });
      setNewComment('');
      fetchComments();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleReply = async (parentId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/events/${eventId}/comments`, { content: replyText, parent_id: parentId });
      setReplyTo(null);
      setReplyText('');
      fetchComments();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/events/${eventId}/comments/${commentId}`);
      fetchComments();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleAddReplay = async () => {
    if (!replayUrl.trim()) return;
    try {
      await api.put(`/events/${eventId}/replay`, { replay_url: replayUrl });
      toast.success('Replay ajoute !');
      setReplayDialogOpen(false);
      setReplayUrl('');
      fetchEvent();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (deleteSeries = false) => {
    try {
      const param = deleteSeries ? '?delete_series=true' : '';
      await api.delete(`/events/${eventId}${param}`);
      toast.success(deleteSeries ? 'Serie supprimee' : 'Evenement supprime');
      navigate('/calendar');
    } catch (e) { toast.error('Erreur'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editEvent) return;
    try {
      const seriesParam = editEvent._updateSeries ? '?update_series=true' : '';
      await api.put(`/events/${eventId}${seriesParam}`, {
        title: editEvent.title,
        description: editEvent.description,
        start_time: new Date(editEvent.start_time).toISOString(),
        end_time: new Date(editEvent.end_time).toISOString(),
        location: editEvent.location,
        event_link: editEvent.event_link || null,
      });
      toast.success(editEvent._updateSeries ? 'Serie modifiee !' : 'Evenement modifie !');
      setEditDialogOpen(false);
      setEditEvent(null);
      fetchEvent();
    } catch (e) { toast.error('Erreur'); }
  };

  const openEditDialog = (updateSeries = false) => {
    setEditEvent({
      title: event.title,
      description: event.description || '',
      start_time: event.start_time ? format(parseISO(event.start_time), "yyyy-MM-dd'T'HH:mm") : '',
      end_time: event.end_time ? format(parseISO(event.end_time), "yyyy-MM-dd'T'HH:mm") : '',
      location: event.location || '',
      event_link: event.event_link || '',
      _updateSeries: updateSeries,
    });
    setEditDialogOpen(true);
  };

  const handleSeriesChoice = (applyToSeries) => {
    setSeriesDialogOpen(false);
    if (seriesAction === 'delete') {
      handleDelete(applyToSeries);
    } else if (seriesAction === 'edit') {
      openEditDialog(applyToSeries);
    }
    setSeriesAction(null);
  };

  // Memoize comments tree (top-level + replies indexed by parent_id) — avoids O(n²) re-filter
  const { topComments, repliesByParent } = useMemo(() => {
    const top = [];
    const byParent = new Map();
    for (const c of comments) {
      if (c.parent_id) {
        if (!byParent.has(c.parent_id)) byParent.set(c.parent_id, []);
        byParent.get(c.parent_id).push(c);
      } else {
        top.push(c);
      }
    }
    return { topComments: top, repliesByParent: byParent };
  }, [comments]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <CalendarIcon className="w-10 h-10 text-primary animate-pulse" />
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="event-detail-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/calendar')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-bold text-lg truncate">{event.title}</h1>
            <p className="text-xs text-muted-foreground">
              {startAt ? format(startAt, 'EEEE d MMMM yyyy', { locale: fr }) : 'Date à préciser'}
            </p>
          </div>
          {isLive && (
            <Badge className="bg-red-500 text-white animate-pulse">EN DIRECT</Badge>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Event Info Card */}
        <Card className="card-base">
          <CardContent className="p-5 space-y-4">
            {event.description && (
              <RichContent html={event.description} className="text-foreground/90 leading-relaxed" />
            )}

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-primary flex-shrink-0" />
                <span>{startAt ? format(startAt, 'EEEE d MMMM yyyy', { locale: fr }) : 'Date à préciser'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                <span>{startAt && endAt ? `${format(startAt, 'HH:mm')} - ${format(endAt, 'HH:mm')}` : 'Horaire à préciser'}</span>
              </div>
              {event.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{event.location}</span>
                </div>
              )}
              {event.created_by_name && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">Cree par {event.created_by_name}</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 pt-2">
              {/* Join / Replay */}
              <div className="flex gap-2">
                {event.event_link && (
                  <Button className="flex-1 bg-primary" onClick={() => window.open(event.event_link, '_blank')}>
                    <Video className="w-4 h-4 mr-2" />
                    {isLive ? 'Rejoindre le live' : 'Lien de reunion'}
                  </Button>
                )}
                {event.replay_url && (
                  <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={() => window.open(event.replay_url, '_blank')}>
                    <Play className="w-4 h-4 mr-2" />
                    Voir le Replay
                  </Button>
                )}
              </div>

              {/* Participate */}
              <Button
                variant={isParticipating ? "outline" : "default"}
                className={`w-full ${isParticipating ? 'border-green-500 text-green-500' : ''}`}
                onClick={handleParticipate}
                data-testid="participate-btn"
              >
                {isParticipating ? (
                  <><UserCheck className="w-4 h-4 mr-2" />Tu participes ({participants.length})</>
                ) : (
                  <><Users className="w-4 h-4 mr-2" />Je participe ({participants.length})</>
                )}
              </Button>

              {/* Admin: edit/delete/replay */}
              {isAdmin && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => {
                    if (event.recurrence_group_id) {
                      setSeriesAction('edit');
                      setSeriesDialogOpen(true);
                    } else {
                      openEditDialog(false);
                    }
                  }}>
                    <Edit className="w-4 h-4 mr-1" />Modifier
                  </Button>
                  <Button variant="destructive" size="sm" className="flex-1" onClick={() => {
                    if (event.recurrence_group_id) {
                      setSeriesAction('delete');
                      setSeriesDialogOpen(true);
                    } else {
                      handleDelete(false);
                    }
                  }}>
                    <Trash2 className="w-4 h-4 mr-1" />Supprimer
                  </Button>
                  {isPast && !event.replay_url && (
                    <Button variant="outline" size="sm" className="border-red-500/50 text-red-500" onClick={() => setReplayDialogOpen(true)}>
                      <Play className="w-4 h-4 mr-1" />+ Replay
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Calendar export */}
        <div>
          <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" onClick={() => setShowCalendarLinks(!showCalendarLinks)}>
            <CalendarIcon className="w-4 h-4" />
            Ajouter a mon calendrier
            <ChevronDown className={`w-3 h-3 transition-transform ${showCalendarLinks ? 'rotate-180' : ''}`} />
          </button>
          {showCalendarLinks && calendarLinks && (
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => window.open(calendarLinks.google_calendar, '_blank')}>
                Google
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(calendarLinks.outlook_hotmail, '_blank')}>
                Outlook
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(calendarLinks.outlook_office365, '_blank')}>
                Office 365
              </Button>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div data-testid="event-comments-section">
          <h3 className="font-heading font-semibold text-base flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-primary" />
            Commentaires ({topComments.length})
          </h3>

          {/* Comment input - VISIBLE AND ACCESSIBLE */}
          <div className="flex gap-2 mb-6" data-testid="comment-input-area">
            <FramedAvatar src={user.avatar_url} fallback={`${user.first_name?.[0]}${user.last_name?.[0]}`} frame={user.profile_frame} role={user.role} seniority={user.seniority} size="sm" />
            <div className="flex-1 flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ecrire un commentaire..."
                className="flex-1 bg-secondary/50"
                onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                data-testid="comment-input"
              />
              <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()} data-testid="comment-submit">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Comments list */}
          <div className="space-y-4">
            {topComments.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">Aucun commentaire. Sois le premier !</p>
            )}
            {topComments.map(comment => (
              <div key={comment.id} className="space-y-3">
                {/* Top-level comment */}
                <div className="flex gap-3">
                  <FramedAvatar src={comment.author_avatar} fallback={comment.author_name?.split(' ').map(n => n[0]).join('')} frame={comment.author_frame} role={comment.author_role} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="bg-secondary/30 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{comment.author_name}</span>
                        <span className="text-xs text-muted-foreground">{comment.created_at ? format(parseISO(comment.created_at), 'dd/MM HH:mm') : ''}</span>
                      </div>
                      <p className="text-sm">{comment.content}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-1 px-2">
                      <button className="text-xs text-muted-foreground hover:text-primary transition-colors" onClick={() => { setReplyTo(comment.id); setReplyText(''); }}>
                        <CornerDownRight className="w-3 h-3 inline mr-1" />Repondre
                      </button>
                      {(comment.author_id === user.id || user.role === 'admin') && (
                        <button className="text-xs text-muted-foreground hover:text-red-500 transition-colors" onClick={() => handleDeleteComment(comment.id)}>
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Replies */}
                {(repliesByParent.get(comment.id) || []).map(reply => (
                  <div key={reply.id} className="flex gap-3 ml-10">
                    <FramedAvatar src={reply.author_avatar} fallback={reply.author_name?.split(' ').map(n => n[0]).join('')} frame={reply.author_frame} role={reply.author_role} size="xs" />
                    <div className="flex-1 min-w-0">
                      <div className="bg-secondary/20 rounded-xl px-3 py-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-xs">{reply.author_name}</span>
                          <span className="text-xs text-muted-foreground">{reply.created_at ? format(parseISO(reply.created_at), 'dd/MM HH:mm') : ''}</span>
                        </div>
                        <p className="text-sm">{reply.content}</p>
                      </div>
                      {(reply.author_id === user.id || user.role === 'admin') && (
                        <button className="text-xs text-muted-foreground hover:text-red-500 mt-1 px-2 transition-colors" onClick={() => handleDeleteComment(reply.id)}>
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Reply input */}
                {replyTo === comment.id && (
                  <div className="flex gap-2 ml-10">
                    <Input
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Repondre..."
                      className="flex-1 text-sm bg-secondary/50"
                      onKeyDown={(e) => e.key === 'Enter' && handleReply(comment.id)}
                      autoFocus
                      data-testid="reply-input"
                    />
                    <Button size="sm" onClick={() => handleReply(comment.id)}>
                      <Send className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>
                      <XCircle className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Series Choice Dialog */}
      <Dialog open={seriesDialogOpen} onOpenChange={setSeriesDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle>{seriesAction === 'delete' ? 'Supprimer' : 'Modifier'} un evenement recurrent</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Button variant="outline" className="w-full justify-start h-auto py-3" onClick={() => handleSeriesChoice(false)}>
              <div className="text-left">
                <p className="font-medium">Cet evenement uniquement</p>
                <p className="text-xs text-muted-foreground">Les autres restent inchanges</p>
              </div>
            </Button>
            <Button variant="outline" className="w-full justify-start h-auto py-3 border-primary/30" onClick={() => handleSeriesChoice(true)}>
              <div className="text-left">
                <p className="font-medium text-primary">Toute la serie</p>
                <p className="text-xs text-muted-foreground">
                  {seriesAction === 'delete' ? 'Supprimer tous les evenements' : 'Modifier titre, description, lieu de tous'}
                </p>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSeriesDialogOpen(false)}>Annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Modifier l'evenement
              {editEvent?._updateSeries && <Badge variant="outline" className="text-primary">Serie</Badge>}
            </DialogTitle>
          </DialogHeader>
          {editEvent && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <Label>Titre *</Label>
                <Input value={editEvent.title} onChange={(e) => setEditEvent({...editEvent, title: e.target.value})} required />
              </div>
              <div>
                <Label>Description</Label>
                <RichTextEditor
                  value={editEvent.description}
                  onChange={(description) => setEditEvent({...editEvent, description})}
                  rows={8}
                  placeholder="Programme, intervenants, lien de connexion…"
                  testId="edit-event-description-editor"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Mise en forme visuelle, ou onglet « HTML » pour coller ton propre code (document complet avec CSS accepté).
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Debut *</Label>
                  <DateTimePicker value={editEvent.start_time} onChange={(start_time) => setEditEvent({...editEvent, start_time})} testId="edit-event-start" />
                </div>
                <div>
                  <Label>Fin *</Label>
                  <DateTimePicker value={editEvent.end_time} onChange={(end_time) => setEditEvent({...editEvent, end_time})} testId="edit-event-end" />
                </div>
              </div>
              <div>
                <Label>Lieu</Label>
                <Input value={editEvent.location} onChange={(e) => setEditEvent({...editEvent, location: e.target.value})} />
              </div>
              <div>
                <Label>Lien Meet/Zoom</Label>
                <Input value={editEvent.event_link} onChange={(e) => setEditEvent({...editEvent, event_link: e.target.value})} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
                <Button type="submit">Sauvegarder</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Replay Dialog */}
      <Dialog open={replayDialogOpen} onOpenChange={setReplayDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-red-500" />Ajouter le Replay
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Lien du replay (YouTube, Loom...)</Label>
              <Input value={replayUrl} onChange={(e) => setReplayUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReplayDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleAddReplay} className="bg-red-500 text-white hover:bg-red-600">
                <Play className="w-4 h-4 mr-2" />Ajouter
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetailPage;
